import React, { ReactNode, useEffect } from "react";
import Footer from "../Footer/Footer";
import NavBar from "../Header/NavBar";
import DisconnectOverlay from "../Widgets/DisconnectOverlay/DisconnectOverlay";

import "../styles/Dropdown.scss";
import "./styles/UserfulColors.css";
import "./styles/UserfulSizes.css";
import "./UserfulPageLayout.scss";

import "../I18nextConfig";

import "@fontsource/inter/100.css";
import "@fontsource/inter/200.css";
import "@fontsource/inter/300.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/inter/800.css";
import "@fontsource/inter/900.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { Bounce, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ErrorOverlay from "../Error/ErrorOverlay";

// COMMON CSS:
import "../styles/Forms.scss";
import "../styles/MultiStateToggle.scss";

import Cookies from "js-cookie";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { UserPermission, UserPermissionType } from "userful-chronos-app-common-js/dist/models/user-permission";
import { getRootPath } from "userful-chronos-app-common-js/dist/routing";
import { CapacityProvider } from "userful-chronos-common-store/dist/diagnostics";
import { EisenhowerProvider } from "userful-chronos-common-store/dist/eisenhower";
import { FailoverProvider } from "userful-chronos-common-store/dist/failover";
import { GroupsProvider } from "userful-chronos-common-store/dist/group";
import { ItemsControlProvider } from "userful-chronos-common-store/dist/itemsControl";
import { ChoppedAssetsProvider, SourceAssetsProvider } from "userful-chronos-common-store/dist/sourceassets";
import { UpdateProgressProvider } from "userful-chronos-common-store/dist/videoengines";
import { DiagnosticContainer } from "../Diagnostic/DiagnosticContainer";
import Flyzone from "../itemsControl/flyzone/Flyzone";
import ToasterProvider from "../toast/ToasterProvider";
import AnomalyNotificationCard from "../Widgets/AnomalyNotification/AnomalyNotificationCard";
import AppAccessDenied from "./AppAccessDenied";
import ConfirmationToast from "./ConfirmationToast";
import FailoverInProgress from "./failoverInProgress/FailoverInProgress";
import LicenseAlertContainer from "./licensealert/LicenseAlertContainer";
import NotificationsContainer from "./NotificationsContainer";
import PerpetualAppCheck from "./perpetualmodecheck/PerpetualAppCheck";
import UpdateProgressContainer from "./updateProgress/UpdateProgressContainer";

interface IProps {
    viewPermissions?: UserPermissionType[];
    showNotification?: boolean;
    showErrors?: boolean;
    hideDisconnectOverlay?: boolean;
    showFooter?: boolean;
    children: ReactNode;
    className?: string;
    version?: string;
    hideNavigation?: boolean;
    navigation?: () => void;
    hideAnomalyAlert?: boolean;
}

const checkPermission = (viewPermissions?: UserPermissionType[], userPermissions?: UserPermission | null): boolean => {
    if (!viewPermissions?.length) {
        return true;
    }

    if (!userPermissions) {
        return false;
    }

    return viewPermissions.some((permission) => Boolean(userPermissions[permission]));
};

const UserfulPageLayout = (props: IProps & React.ComponentProps<typeof NavBar>) => {
    const {
        viewPermissions,
        showNotification = true,
        showErrors = true,
        hideDisconnectOverlay,
        showFooter,
        children,
        hideNavigation,
        version,
        hideAnomalyAlert,
        ...navBarProps
    } = props;

    const { appID, userPermission } = getGlobalStates();
    const canView = checkPermission(viewPermissions, userPermission);

    useEffect(() => {
        if (!canView) {
            window.location.replace(getRootPath() + window.location.search);
        }
    }, [canView]);

    const content = (
        <>
            {!hideNavigation && <NavBar {...navBarProps} showNotification={showNotification} />}
            <DisconnectOverlay disabled={hideDisconnectOverlay} />
            {canView ? (
                <>
                    {showErrors && <ErrorOverlay />}
                    <div className="main-content-whole">
                        {showNotification ? <PerpetualAppCheck>{children}</PerpetualAppCheck> : children}
                    </div>
                    {showFooter && <Footer version={version} />}
                    {showNotification && (
                        <>
                            <ToastContainer transition={Bounce} containerId="uploadFileContainer" />
                            <NotificationsContainer />
                            <DiagnosticContainer />
                            <ConfirmationToast />
                            <SourceAssetsProvider appID={appID.value}>
                                <UpdateProgressProvider>
                                    <UpdateProgressContainer />
                                </UpdateProgressProvider>
                            </SourceAssetsProvider>
                            <LicenseAlertContainer />
                            {!hideAnomalyAlert &&
                                userPermission?.isTabletCtrlUser &&
                                !Cookies.get("hideAnomalyAlert") && <AnomalyNotificationCard />}
                        </>
                    )}
                </>
            ) : (
                <div className="main-content-whole">
                    <AppAccessDenied />
                </div>
            )}
            {showNotification && <FailoverInProgress />}
        </>
    );

    // Is safe for web-socket Providers
    const wrappedContent =
        canView && showNotification ? (
            <EisenhowerProvider>
                <GroupsProvider>
                    <ChoppedAssetsProvider>{content}</ChoppedAssetsProvider>
                </GroupsProvider>
            </EisenhowerProvider>
        ) : (
            content
        );

    // Is NOT safe for web-socket Providers
    return (
        <ToasterProvider>
            <ItemsControlProvider>
                <Flyzone>
                    <FailoverProvider>
                        <CapacityProvider>{wrappedContent}</CapacityProvider>
                    </FailoverProvider>
                </Flyzone>
            </ItemsControlProvider>
        </ToasterProvider>
    );
};

export default UserfulPageLayout;
